/*
 * Regras puras do Acompanhamento Estatístico do administrativo (sem React): cards de situação,
 * itens de documentos e de período, evolução diária das inscrições e concorrência por vaga.
 */

import {categoriaSituacao} from './inscricao/situacaoInscricao.js';
import {SEM_VALOR} from './inscricao/documentoInscricao.js';

export const SITUACOES_DESTACADAS = [
    {codigo: 'H', titulo: 'Habilitados'},
    {codigo: 'P', titulo: 'Com pendência'},
    {codigo: 'I', titulo: 'Inabilitados'}
];

export const TURNO_EAD = 'EAD';

const codigoDe = linha => String(linha && linha.codigo ? linha.codigo : '').toUpperCase();

const numero = valor => Number(valor) || 0;

const texto = valor => (valor === null || valor === undefined ? '' : String(valor));

/* Situações */

export const totalSituacoes = situacoes => (situacoes || []).reduce((soma, linha) => soma + numero(linha && linha.quantidade), 0);

export const quantidadeDaSituacao = (situacoes, codigo) => {
    const alvo = String(codigo || '').toUpperCase();
    return (situacoes || []).reduce((soma, linha) => (codigoDe(linha) === alvo ? soma + numero(linha.quantidade) : soma), 0);
};

export const textoPercentualInscritos = (qtd, total) => {
    if (!(total > 0)) return undefined;
    const quantidade = numero(qtd);
    if (quantidade === 0) return '0% dos inscritos';
    const percentual = (quantidade / total) * 100;
    if (percentual > 0 && percentual < 1) return 'menos de 1% dos inscritos';
    const arredondado = Math.round(percentual);
    return `${arredondado === 100 && quantidade < total ? 99 : arredondado}% dos inscritos`;
};

export const montarCardsSituacao = situacoes => {
    const total = totalSituacoes(situacoes);
    return SITUACOES_DESTACADAS.map(({codigo, titulo}) => {
        const quantidade = quantidadeDaSituacao(situacoes, codigo);
        return {
            codigo,
            titulo,
            quantidade,
            valorSecundario: textoPercentualInscritos(quantidade, total),
            categoria: categoriaSituacao(codigo)
        };
    });
};

/* Documentos */

export const montarItensDocumentos = doc => [
    {chave: 'APROVADO', rotulo: 'Aprovados', valor: numero(doc && doc.aprovados), categoria: 'sucesso'},
    {chave: 'NEGADO', rotulo: 'Negados', valor: numero(doc && doc.negados), categoria: 'erro'},
    {chave: 'PARA_ANALISAR', rotulo: 'Para analisar', valor: numero(doc && doc.semAvaliacao), categoria: 'pendente'},
    {
        chave: 'COM_PENDENCIA',
        rotulo: 'Inscrições com pendência',
        valor: numero(doc && doc.inscricoesComPendencia),
        categoria: 'atencao',
        secundario: textoPendencias(numero(doc && doc.pendencias))
    }
];

/* Período */

export const montarItensPeriodo = inscricoes => [
    {chave: 'DIA', rotulo: 'Hoje', valor: numero(inscricoes && inscricoes.quantidadeNoDia), categoria: 'info'},
    {chave: 'SEMANA', rotulo: 'Nesta semana', valor: numero(inscricoes && inscricoes.quantidadeNaSemana), categoria: 'info'},
    {chave: 'MES', rotulo: 'Neste mês', valor: numero(inscricoes && inscricoes.quantidadeNoMes), categoria: 'info'}
];

export const textoPendencias = n => {
    if (!n) return undefined;
    return n === 1 ? '1 documento pendente' : `${n} documentos pendentes`;
};

/* Evolução diária: datas no formato yyyy-MM-dd, sempre em UTC */

const MS_DIA = 24 * 60 * 60 * 1000;

const partesData = data => {
    const casa = typeof data === 'string' && /^\d{4}-\d{2}-\d{2}$/.exec(data);
    if (!casa) return null;
    const ano = Number(data.slice(0, 4));
    const mes = Number(data.slice(5, 7));
    const dia = Number(data.slice(8, 10));
    const ms = Date.UTC(ano, mes - 1, dia);
    const d = new Date(ms);
    if (d.getUTCFullYear() !== ano || d.getUTCMonth() !== mes - 1 || d.getUTCDate() !== dia) return null;
    return {ano, mes, dia, ms};
};

const formatarIso = (ano, mes, dia) => {
    const d = new Date(Date.UTC(ano, mes - 1, dia));
    const aa = String(d.getUTCFullYear()).padStart(4, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${aa}-${mm}-${dd}`;
};

export const preencherDias = dias => {
    if (!Array.isArray(dias) || dias.length === 0) return [];
    const porData = new Map();
    dias.forEach(item => {
        const partes = partesData(item && item.data);
        if (!partes) return;
        const chave = formatarIso(partes.ano, partes.mes, partes.dia);
        porData.set(chave, (porData.get(chave) || 0) + numero(item.quantidade));
    });
    if (porData.size === 0) return [];
    const datas = [...porData.keys()].map(partesData);
    const inicio = Math.min(...datas.map(p => p.ms));
    const fim = Math.max(...datas.map(p => p.ms));
    const resultado = [];
    for (let ms = inicio; ms <= fim; ms += MS_DIA) {
        const d = new Date(ms);
        const chave = formatarIso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
        resultado.push({data: chave, quantidade: porData.get(chave) || 0});
    }
    return resultado;
};

export const rotuloDia = data => {
    const p = partesData(data);
    return p ? `${String(p.dia).padStart(2, '0')}/${String(p.mes).padStart(2, '0')}` : texto(data);
};

export const formatarData = data => {
    const p = partesData(data);
    return p ? `${String(p.dia).padStart(2, '0')}/${String(p.mes).padStart(2, '0')}/${p.ano}` : texto(data);
};

/* Concorrência */

export const relacaoInscritosPorVaga = (inscritos, vagas) => (vagas > 0 ? inscritos / vagas : null);

export const formatarRelacao = valor => (valor === null || valor === undefined ? SEM_VALOR : valor.toFixed(1).replace('.', ','));

export const montarLinhasConcorrencia = vagas => {
    if (!vagas) return [];
    const linhas = vagas.map(linha => {
        const totalVagas = numero(linha.vagas);
        const inscritos = numero(linha.inscritos);
        const razao = relacaoInscritosPorVaga(inscritos, totalVagas);
        return {
            ...linha,
            turno: linha.turno || TURNO_EAD,
            vagas: totalVagas,
            inscritos,
            inscritosPorVaga: razao,
            inscritosPorVagaTexto: formatarRelacao(razao)
        };
    });
    return linhas.sort((a, b) => {
        const ra = a.inscritosPorVaga;
        const rb = b.inscritosPorVaga;
        if (ra !== rb) {
            if (ra === null) return 1;
            if (rb === null) return -1;
            return rb - ra;
        }
        if (a.inscritos !== b.inscritos) return b.inscritos - a.inscritos;
        return (
            texto(a.curso).localeCompare(texto(b.curso), 'pt-BR') ||
            texto(a.campus).localeCompare(texto(b.campus), 'pt-BR') ||
            texto(a.turno).localeCompare(texto(b.turno), 'pt-BR')
        );
    });
};
